/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useMemo } from 'react';
import { useAuth } from '@vaas/react-ui-kit/auth';

import { getRoles, isDealerFromClaims } from '@/lib/tokenClaims';
import {
  ALL_INTERNAL_ROLES,
  applyServiceBaseline,
  capabilitiesFor,
  hasInternalRole,
  isDealerRole,
  isSuperuserRoles,
  type Capability,
} from '@/lib/roles';
import { useRoleView } from '@/stores/roleView';

/** Role strings that gate the UI: view-as override, full internal set for switchers, else token roles with service baseline. */
export function useEffectiveRoles(): string[] {
  const { account } = useAuth();
  const override = useRoleView((s) => s.override);
  const canSwitch = useCanSwitchRoles();
  if (override) return override;
  if (canSwitch) return [...ALL_INTERNAL_ROLES];
  if (!account) return [];
  const roles = getRoles();
  return applyServiceBaseline(roles, isDealerFromClaims() && !hasInternalRole(roles));
}

/** The current user's (possibly overridden) capability set. */
export function useCapabilities(): Set<Capability> {
  const roles = useEffectiveRoles();
  const key = roles.join(',');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` captures the role list identity.
  return useMemo(() => capabilitiesFor(roles), [key]);
}

/** True when the user has the given capability (respecting any active role override). */
export function useHasCapability(capability: Capability): boolean {
  return useCapabilities().has(capability);
}

/** True when the (possibly overridden) roles amount to an eDiagnostics superuser. */
export function useIsSuperuser(): boolean {
  const roles = useEffectiveRoles();
  const key = roles.join(',');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` captures the role list identity.
  return useMemo(() => isSuperuserRoles(roles), [key]);
}

/** Whether the profile "view as" switcher is offered (keyed off real token roles, not override). */
export function useCanSwitchRoles(): boolean {
  const { account, mode } = useAuth();
  if (mode !== 'msal') return true;
  if (!account) return false;
  const real = getRoles();
  const caps = capabilitiesFor(real);
  return caps.has('admin.dealerAccess') || isSuperuserRoles(real);
}

/** True for pure external dealers once the account resolves; fails closed while loading. */
export function useIsDealerUser(): boolean {
  const { account } = useAuth();
  const override = useRoleView((s) => s.override);
  if (override) return override.some(isDealerRole) && !hasInternalRole(override);
  if (!account) return false;
  return isDealerFromClaims() && !hasInternalRole(getRoles());
}

/** Complement of {@link useIsDealerUser}; honors view-as override and fails closed while loading. */
export function useIsInternalUser(): boolean {
  const { account } = useAuth();
  const override = useRoleView((s) => s.override);
  if (override) return !(override.some(isDealerRole) && !hasInternalRole(override));
  // Until the account resolves we can't confirm the user; default to external so internal-only
  // metadata (program code / engineering release) is never exposed during loading.
  if (!account) return false;
  return !(isDealerFromClaims() && !hasInternalRole(getRoles()));
}
